import { Link, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { Phone, Plus, Upload } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { CONSENT_BASES, CONSENT_BASIS_LABELS, CONTACT_IMPORT_FIELDS, type ConsentBasis } from '@salesbee/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  formatDate,
  formatDateTime,
  formatRelative,
  segmentedItemClass,
  segmentedListClass,
  useToast,
} from '@salesbee/ui';
import { downloadCsv, money, OutcomeBadge, QueryView, Section } from '../components/common';
import { errorMessage, trpc } from '../lib/trpc';

const CONSENT_OPTIONS = CONSENT_BASES.map((c) => ({ value: c, label: CONSENT_BASIS_LABELS[c] }));

export function CrmTabs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tabs = [
    ['/crm/contacts', 'Contacts'],
    ['/crm/companies', 'Companies'],
    ['/crm/pipeline', 'Pipeline'],
    ['/crm/tasks', 'Tasks'],
    ['/crm/lists', 'Lists & segments'],
    ['/crm/import', 'Import / export'],
  ] as const;
  return (
    <div className={cn(segmentedListClass, 'mb-6 overflow-x-auto')}>
      {tabs.map(([to, label]) => (
        <Link key={to} to={to} className={segmentedItemClass(pathname.startsWith(to))}>
          {label}
        </Link>
      ))}
    </div>
  );
}

function ConsentBadge({ basis }: { basis: string }) {
  const variant = basis === 'express_consent' ? 'success' : basis === 'none' ? 'warning' : 'info';
  return <Badge variant={variant}>{CONSENT_BASIS_LABELS[basis as ConsentBasis] ?? basis}</Badge>;
}

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

function NewContactDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const create = trpc.crm.contacts.create.useMutation();
  const utils = trpc.useUtils();
  const { success } = useToast();
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', phone: '', city: '', region: '', consentBasis: 'none' as ConsentBasis });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="New contact">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(
              { firstName: f.firstName, lastName: f.lastName, email: f.email || null, phone: f.phone || null, consentBasis: f.consentBasis, address: f.city || f.region ? { city: f.city, region: f.region } : null },
              {
                onSuccess: async () => {
                  success('Contact created');
                  onOpenChange(false);
                  await utils.crm.contacts.list.invalidate();
                },
              },
            );
          }}
        >
          {create.error ? <Alert variant="destructive">{errorMessage(create.error)}</Alert> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="First name"><Input value={f.firstName} onChange={set('firstName')} /></Field>
            <Field label="Last name"><Input value={f.lastName} onChange={set('lastName')} /></Field>
            <Field label="Email"><Input type="email" value={f.email} onChange={set('email')} /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={set('phone')} /></Field>
            <Field label="City"><Input value={f.city} onChange={set('city')} /></Field>
            <Field label="Province/State (code)"><Input value={f.region} onChange={set('region')} placeholder="ON" /></Field>
          </div>
          <Field label="Legal basis for calling" description="Drives the national DNC check and whether an AI voice may call.">
            <Select value={f.consentBasis} onChange={set('consentBasis')} options={CONSENT_OPTIONS} />
          </Field>
          <Button type="submit" loading={create.isPending}>Create contact</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ContactsPage() {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const list = trpc.crm.contacts.list.useQuery({ search: search || undefined, limit: 100 });
  const navigate = useNavigate();
  return (
    <div>
      <PageHeader title="CRM" description="Contacts, companies and deals." actions={<Button onClick={() => setOpen(true)}><Plus /> New contact</Button>} />
      <CrmTabs />
      <Input className="mb-4 max-w-sm" placeholder="Search name, email or phone" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search contacts" />
      <QueryView query={list}>
        {(data) =>
          data.items.length === 0 ? (
            <EmptyState title="No contacts" description="Create one, import a CSV or import prospects." />
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Consent</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Last contacted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((c) => (
                    <TableRow key={c.id} className="cursor-pointer hover:bg-muted/50" onClick={() => void navigate({ to: '/crm/contacts/$contactId', params: { contactId: c.id } })}>
                      <TableCell className="font-medium">
                        {c.displayName} {c.doNotCallAt ? <Badge variant="destructive">DNC</Badge> : null}
                      </TableCell>
                      <TableCell>{c.companyName ?? '—'}</TableCell>
                      <TableCell className="font-mono text-[13px]">{c.phone ?? c.mobilePhone ?? '—'}</TableCell>
                      <TableCell><ConsentBadge basis={c.consentBasis} /></TableCell>
                      <TableCell className="capitalize">{c.lifecycleStage}</TableCell>
                      <TableCell>{c.lastContactedAt ? formatRelative(c.lastContactedAt) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )
        }
      </QueryView>
      <NewContactDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function ClickToCall({ contactId, disabled }: { contactId: string; disabled: boolean }) {
  const start = trpc.outbound.calls.startManual.useMutation();
  const complete = trpc.outbound.calls.completeManual.useMutation();
  const utils = trpc.useUtils();
  const { success, error } = useToast();
  const [active, setActive] = useState<{ callId: string; dialUri: string; disclosure: string | null } | null>(null);
  const [outcome, setOutcome] = useState('connected');
  const [notes, setNotes] = useState('');
  const [minutes, setMinutes] = useState('2');
  return (
    <>
      <Button
        variant="outline"
        disabled={disabled}
        loading={start.isPending}
        onClick={() =>
          start.mutate(
            { contactId },
            {
              onSuccess: (r) => {
                setActive(r);
                window.location.href = r.dialUri;
              },
              onError: (e) => error('Call blocked', errorMessage(e)),
            },
          )
        }
      >
        <Phone /> Call
      </Button>
      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent title="Log the call">
          {active?.disclosure ? <Alert variant="info" title="Say this first (recording disclosure)">{active.disclosure}</Alert> : null}
          <div className="mt-4 space-y-4">
            <Field label="Outcome">
              <Select value={outcome} onChange={(e) => setOutcome(e.target.value)} options={['connected', 'voicemail', 'no_answer', 'busy', 'interested', 'meeting_booked', 'not_interested', 'callback_requested', 'do_not_call'].map((o) => ({ value: o, label: o.replace(/_/g, ' ') }))} />
            </Field>
            <Field label="Minutes"><Input type="number" min={0} value={minutes} onChange={(e) => setMinutes(e.target.value)} /></Field>
            <Field label="Notes"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
            <Button
              loading={complete.isPending}
              onClick={() =>
                active &&
                complete.mutate(
                  { callId: active.callId, outcome: outcome as 'connected', durationSeconds: Math.round(Number(minutes) * 60), notes: notes || null },
                  {
                    onSuccess: async () => {
                      success('Call logged');
                      setActive(null);
                      await utils.crm.contacts.get.invalidate();
                    },
                  },
                )
              }
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ContactDetailPage() {
  const { contactId } = useParams({ strict: false }) as { contactId: string };
  const contact = trpc.crm.contacts.get.useQuery({ id: contactId });
  const update = trpc.crm.contacts.update.useMutation();
  const log = trpc.crm.activities.create.useMutation();
  const utils = trpc.useUtils();
  const { success } = useToast();
  const [note, setNote] = useState('');
  return (
    <div>
      <CrmTabs />
      <QueryView query={contact}>
        {(c) => (
          <div className="space-y-6">
            <PageHeader
              title={c.displayName}
              description={[c.jobTitle, c.company?.name].filter(Boolean).join(' · ') || undefined}
              actions={<ClickToCall contactId={c.id} disabled={!!c.doNotCallAt || !c.e164} />}
            />
            {c.doNotCallAt ? <Alert variant="destructive">On the do-not-call list since {formatDate(c.doNotCallAt)}. No calls or texts.</Alert> : null}
            <div className="grid gap-6 lg:grid-cols-3">
              <Card>
                <CardContent className="space-y-3 py-6 text-[14px]">
                  <p><span className="text-muted-foreground">Email:</span> {c.email ?? '—'}</p>
                  <p><span className="text-muted-foreground">Phone:</span> <span className="font-mono">{c.e164 ?? c.phone ?? '—'}</span></p>
                  <p><span className="text-muted-foreground">Location:</span> {[c.address?.city, c.region, c.country].filter(Boolean).join(', ') || '—'}</p>
                  <p><span className="text-muted-foreground">Time zone:</span> {c.timezone ?? 'from province/state'}</p>
                  <Field label="Legal basis for calling">
                    <Select
                      value={c.consentBasis}
                      onChange={(e) => update.mutate({ id: c.id, consentBasis: e.target.value as ConsentBasis }, { onSuccess: async () => { success('Consent updated (audited)'); await utils.crm.contacts.get.invalidate(); } })}
                      options={CONSENT_OPTIONS}
                    />
                  </Field>
                  {c.consentNote ? <p className="text-muted-foreground">{c.consentNote}</p> : null}
                  <Field label="Tags"><p>{c.tags.length ? c.tags.join(', ') : '—'}</p></Field>
                </CardContent>
              </Card>
              <div className="space-y-6 lg:col-span-2">
                <Section title="Deals">
                  <Card>
                    <CardContent className="divide-y divide-border py-2">
                      {c.deals.length === 0 ? <p className="py-3 text-sm text-muted-foreground">No deals.</p> : null}
                      {c.deals.map((d) => (
                        <div key={d.id} className="flex justify-between py-3 text-[14px]">
                          <span>{d.title}</span>
                          <span className="font-mono">{money(d.valueCents)} · <span className="capitalize">{d.status}</span></span>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </Section>
                <Section title="Calls">
                  <Card>
                    <CardContent className="divide-y divide-border py-2">
                      {c.calls.length === 0 ? <p className="py-3 text-sm text-muted-foreground">No calls.</p> : null}
                      {c.calls.map((call) => (
                        <details key={call.id} className="py-3 text-[14px]">
                          <summary className="flex cursor-pointer items-center justify-between gap-2">
                            <span>{call.mode === 'ai' ? 'AI call' : 'Call'} · {formatDateTime(call.createdAt)}</span>
                            <OutcomeBadge outcome={call.outcome} />
                          </summary>
                          <div className="mt-2 space-y-1 text-muted-foreground">
                            {call.summary ? <p>{call.summary}</p> : null}
                            {call.transcript.map((t, i) => (
                              <p key={i}><span className="font-medium text-foreground">{t.speaker === 'agent' ? 'Agent' : 'Contact'}:</span> {t.text}</p>
                            ))}
                          </div>
                        </details>
                      ))}
                    </CardContent>
                  </Card>
                </Section>
                <Section title="Timeline">
                  <Card>
                    <CardContent className="space-y-3 py-4">
                      <div className="flex gap-2">
                        <Input placeholder="Add a note" value={note} onChange={(e) => setNote(e.target.value)} />
                        <Button disabled={!note.trim()} loading={log.isPending} onClick={() => log.mutate({ type: 'note', body: note, contactId: c.id }, { onSuccess: async () => { setNote(''); await utils.crm.contacts.get.invalidate(); } })}>Add</Button>
                      </div>
                      {c.timeline.map((a) => (
                        <div key={a.id} className="border-l-2 border-border pl-3 text-[14px]">
                          <p className="font-medium">{a.subject ?? a.type.replace(/_/g, ' ')}</p>
                          {a.body ? <p className="text-muted-foreground">{a.body}</p> : null}
                          <p className="text-xs text-muted-foreground">{formatDateTime(a.occurredAt)}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </Section>
              </div>
            </div>
          </div>
        )}
      </QueryView>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export function CompaniesPage() {
  const [search, setSearch] = useState('');
  const list = trpc.crm.companies.list.useQuery({ search: search || undefined, limit: 100 });
  const create = trpc.crm.companies.create.useMutation();
  const utils = trpc.useUtils();
  const [name, setName] = useState('');
  const navigate = useNavigate();
  return (
    <div>
      <PageHeader title="CRM" description="Companies you sell to." />
      <CrmTabs />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input className="max-w-sm" placeholder="Search companies" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search companies" />
        <Input className="max-w-xs" placeholder="New company name" value={name} onChange={(e) => setName(e.target.value)} />
        <Button disabled={!name.trim()} loading={create.isPending} onClick={() => create.mutate({ name, tags: [], custom: {} }, { onSuccess: async () => { setName(''); await utils.crm.companies.list.invalidate(); } })}>
          <Plus /> Add
        </Button>
      </div>
      <QueryView query={list}>
        {(data) => (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Location</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-muted/50" onClick={() => void navigate({ to: '/crm/companies/$companyId', params: { companyId: c.id } })}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.industry ?? '—'}</TableCell>
                    <TableCell className="font-mono text-[13px]">{c.phone ?? '—'}</TableCell>
                    <TableCell>{[c.address?.city, c.address?.region].filter(Boolean).join(', ') || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </QueryView>
    </div>
  );
}

export function CompanyDetailPage() {
  const { companyId } = useParams({ strict: false }) as { companyId: string };
  const company = trpc.crm.companies.get.useQuery({ id: companyId });
  return (
    <div>
      <CrmTabs />
      <QueryView query={company}>
        {(c) => (
          <div className="space-y-6">
            <PageHeader title={c.name} description={[c.industry, c.website].filter(Boolean).join(' · ') || undefined} />
            <Section title="People">
              <Card>
                <CardContent className="divide-y divide-border py-2">
                  {c.contacts.map((p) => (
                    <Link key={p.id} to="/crm/contacts/$contactId" params={{ contactId: p.id }} className="flex justify-between py-3 text-[14px] hover:underline">
                      <span>{p.displayName}</span>
                      <span className="text-muted-foreground">{p.jobTitle ?? ''}</span>
                    </Link>
                  ))}
                  {c.contacts.length === 0 ? <p className="py-3 text-sm text-muted-foreground">No contacts.</p> : null}
                </CardContent>
              </Card>
            </Section>
            <Section title="Deals">
              <Card>
                <CardContent className="divide-y divide-border py-2">
                  {c.deals.map((d) => (
                    <div key={d.id} className="flex justify-between py-3 text-[14px]">
                      <span>{d.title}</span>
                      <span className="font-mono">{money(d.valueCents)}</span>
                    </div>
                  ))}
                  {c.deals.length === 0 ? <p className="py-3 text-sm text-muted-foreground">No deals.</p> : null}
                </CardContent>
              </Card>
            </Section>
          </div>
        )}
      </QueryView>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline board
// ---------------------------------------------------------------------------

export function PipelinePage() {
  const board = trpc.crm.deals.list.useQuery({});
  const move = trpc.crm.deals.move.useMutation();
  const create = trpc.crm.deals.create.useMutation();
  const utils = trpc.useUtils();
  const { error } = useToast();
  const [title, setTitle] = useState('');
  const [value, setValue] = useState('');
  return (
    <div>
      <PageHeader title="CRM" description="Deal pipeline. Call outcomes move deals forward automatically." />
      <CrmTabs />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input className="max-w-xs" placeholder="New deal title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Input className="max-w-[10rem]" type="number" placeholder="Value (CAD)" value={value} onChange={(e) => setValue(e.target.value)} />
        <Button disabled={!title.trim()} loading={create.isPending} onClick={() => create.mutate({ title, valueCents: Math.round(Number(value || 0) * 100), tags: [] }, { onSuccess: async () => { setTitle(''); setValue(''); await utils.crm.deals.list.invalidate(); } })}>
          <Plus /> Add deal
        </Button>
      </div>
      <QueryView query={board}>
        {(b) => (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {b.stages.map((s) => {
              const items = b.items.filter((d) => d.stageId === s.id);
              return (
                <div key={s.id} className="w-72 shrink-0 space-y-3" aria-label={`Stage ${s.name}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-[14px] font-semibold">
                      <span className="size-2.5 rounded-full" style={{ background: s.color ?? 'var(--color-primary)' }} />
                      {s.name}
                    </h3>
                    <span className="font-mono text-[12px] text-muted-foreground">{money(items.reduce((n, d) => n + d.valueCents, 0))}</span>
                  </div>
                  {items.map((d) => (
                    <Card key={d.id}>
                      <CardContent className="space-y-2 py-4 text-[14px]">
                        <p className="font-medium">{d.title}</p>
                        <p className="text-muted-foreground">{d.contactName ?? d.companyName ?? '—'}</p>
                        <p className="font-mono">{money(d.valueCents)}</p>
                        <Select
                          aria-label={`Move ${d.title}`}
                          value={d.stageId}
                          onChange={(e) => move.mutate({ id: d.id, stageId: e.target.value }, { onSuccess: () => void utils.crm.deals.list.invalidate(), onError: (er) => error('Could not move', errorMessage(er)) })}
                          options={b.stages.map((x) => ({ value: x.id, label: x.name }))}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </QueryView>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tasks, lists, import / export
// ---------------------------------------------------------------------------

export function TasksPage() {
  const [mine, setMine] = useState(true);
  const tasks = trpc.crm.tasks.list.useQuery({ status: 'todo', mine, limit: 100 });
  const update = trpc.crm.tasks.update.useMutation();
  const create = trpc.crm.tasks.create.useMutation();
  const utils = trpc.useUtils();
  const [title, setTitle] = useState('');
  return (
    <div>
      <PageHeader title="CRM" description="Follow-ups, including the ones calls create." />
      <CrmTabs />
      <div className="mb-4 flex flex-wrap gap-2">
        <Select className="w-44" value={mine ? 'mine' : 'all'} onChange={(e) => setMine(e.target.value === 'mine')} options={[{ value: 'mine', label: 'My tasks' }, { value: 'all', label: 'Everyone' }]} />
        <Input className="max-w-sm" placeholder="New task" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Button disabled={!title.trim()} onClick={() => create.mutate({ title, priority: 'normal' }, { onSuccess: async () => { setTitle(''); await utils.crm.tasks.list.invalidate(); } })}><Plus /> Add</Button>
      </div>
      <QueryView query={tasks}>
        {(data) => (
          <Card>
            <CardContent className="divide-y divide-border py-2">
              {data.items.length === 0 ? <p className="py-4 text-sm text-muted-foreground">Nothing to do.</p> : null}
              {data.items.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 py-3 text-[14px]">
                  <div>
                    <p className="font-medium">{t.title} {t.priority === 'high' ? <Badge variant="warning">High</Badge> : null}</p>
                    <p className="text-muted-foreground">{[t.contactName, t.dealTitle, t.dueAt ? `due ${formatRelative(t.dueAt)}` : null].filter(Boolean).join(' · ')}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => update.mutate({ id: t.id, status: 'done' }, { onSuccess: () => void utils.crm.tasks.list.invalidate() })}>Done</Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </QueryView>
    </div>
  );
}

export function ListsPage() {
  const lists = trpc.crm.lists.list.useQuery();
  const create = trpc.crm.lists.create.useMutation();
  const utils = trpc.useUtils();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'static' | 'segment'>('static');
  const [region, setRegion] = useState('');
  const [consent, setConsent] = useState('');
  return (
    <div>
      <PageHeader title="CRM" description="Static lists and dynamic segments: the audiences for campaigns and sequences." />
      <CrmTabs />
      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-end gap-3 py-5">
          <Field label="Name" className="min-w-56"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Type"><Select value={kind} onChange={(e) => setKind(e.target.value as 'static')} options={[{ value: 'static', label: 'Static list' }, { value: 'segment', label: 'Segment (filters)' }]} /></Field>
          {kind === 'segment' ? (
            <>
              <Field label="Province/State codes"><Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="ON,BC" /></Field>
              <Field label="Consent"><Select value={consent} onChange={(e) => setConsent(e.target.value)} placeholder="Any" options={CONSENT_OPTIONS} /></Field>
            </>
          ) : null}
          <Button
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                { name, kind, filters: kind === 'segment' ? { regions: region ? region.split(',').map((r) => r.trim().toUpperCase()) : undefined, consentBases: consent ? [consent as ConsentBasis] : undefined, hasPhone: true } : {} },
                { onSuccess: async () => { setName(''); await utils.crm.lists.list.invalidate(); } },
              )
            }
          >
            <Plus /> Create
          </Button>
        </CardContent>
      </Card>
      <QueryView query={lists}>
        {(data) => (
          <Card>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Contacts</TableHead><TableHead>Description</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {data.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-medium"><Link to="/crm/contacts" className="hover:underline">{l.name}</Link></TableCell>
                    <TableCell><Badge variant={l.kind === 'segment' ? 'info' : 'default'}>{l.kind}</Badge></TableCell>
                    <TableCell className="font-mono">{l.size}</TableCell>
                    <TableCell className="text-muted-foreground">{l.description ?? ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </QueryView>
    </div>
  );
}

export function ImportPage() {
  const [csv, setCsv] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState<ConsentBasis>('none');
  const guess = trpc.crm.importExport.guessMapping.useQuery({ csv }, { enabled: csv.length > 0 });
  const run = trpc.crm.importExport.importContacts.useMutation();
  const exp = trpc.crm.importExport.export.useMutation();
  const effective = { ...(guess.data?.mapping ?? {}), ...mapping };
  const onFile = async (file: File | undefined) => {
    if (file) setCsv(await file.text());
  };
  const exportBtn = (entity: 'contacts' | 'companies' | 'deals', label: ReactNode) => (
    <Button variant="outline" loading={exp.isPending} onClick={() => exp.mutate({ entity }, { onSuccess: (r) => downloadCsv(r.filename, r.csv) })}>
      {label}
    </Button>
  );
  return (
    <div>
      <PageHeader title="CRM" description="Import contacts from a CSV; export anything." />
      <CrmTabs />
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardContent className="space-y-4 py-6">
            <h2 className="flex items-center gap-2 text-[17px] font-semibold"><Upload className="size-4" /> Import contacts</h2>
            <input type="file" accept=".csv,text/csv" aria-label="CSV file" onChange={(e) => void onFile(e.target.files?.[0])} />
            <Textarea rows={5} placeholder="…or paste CSV here (first row = headers)" value={csv} onChange={(e) => setCsv(e.target.value)} />
            {guess.data ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{guess.data.rows} rows. Map the columns:</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {guess.data.headers.map((h) => (
                    <Field key={h} label={h}>
                      <Select value={effective[h] ?? ''} onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))} placeholder="Ignore" options={CONTACT_IMPORT_FIELDS.map((f) => ({ value: f, label: f }))} />
                    </Field>
                  ))}
                </div>
                <Field label="Legal basis when the file has none"><Select value={consent} onChange={(e) => setConsent(e.target.value as ConsentBasis)} options={CONSENT_OPTIONS} /></Field>
                <Button
                  loading={run.isPending}
                  onClick={() => run.mutate({ csv, mapping: Object.fromEntries(Object.entries(effective).filter(([, v]) => v)) as Record<string, 'firstName'>, dedupe: 'skip', defaultConsentBasis: consent })}
                >
                  Import
                </Button>
              </div>
            ) : null}
            {run.error ? <Alert variant="destructive">{errorMessage(run.error)}</Alert> : null}
            {run.data ? (
              <Alert variant="success" title="Import finished">
                Created {run.data.created}, updated {run.data.updated}, skipped {run.data.skipped} duplicates.
                {run.data.errors.length ? ` Errors: ${run.data.errors.map((e) => `row ${e.row}: ${e.message}`).join('; ')}` : ''}
              </Alert>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex flex-col gap-3 py-6">
            <h2 className="text-[17px] font-semibold">Export CSV</h2>
            {exportBtn('contacts', 'Contacts')}
            {exportBtn('companies', 'Companies')}
            {exportBtn('deals', 'Deals')}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
